import { useMemo } from "react";

/**
 * Categorizes MRs into review board columns based on their state and review status.
 *
 * Columns (in order):
 * 1. ignored - MRs manually ignored by user (unless closed/merged)
 * 2. openAssigned - Open MRs where I'm a reviewer, not yet categorized elsewhere
 * 3. approvedBlocked - I approved but merge checks fail
 * 4. readyToMerge - All merge checks pass
 * 5. recentlyClosed - Recently merged/closed MRs
 */

// Sort by: drafts last, then by specified date field descending
const createSorter = (sortOption) => (a, b) => {
	// Drafts go to the bottom
	if (a.draft && !b.draft) return 1;
	if (!a.draft && b.draft) return -1;
	// Then sort by the selected date field
	const dateField = sortOption === "createdAt" ? "createdAt" : "updatedAt";
	return new Date(b[dateField]) - new Date(a[dateField]);
};

export const useReviewBoardCategorization = (
	pendingReviewMRs,
	myOpenMRs,
	recentlyClosedMRs,
	ignoredIds,
	sortOption = "updatedAt",
) => {
	const categorized = useMemo(() => {
		const columns = {
			ignored: [],
			openAssigned: [],
			approvedBlocked: [],
			readyToMerge: [],
			recentlyClosed: [],
		};

		// Combine all open MRs (pending review + my open)
		// Use a Set to avoid duplicates (MR might be in both lists)
		const seenIds = new Set();
		const allOpenMRs = [];

		for (const mr of [...pendingReviewMRs, ...myOpenMRs]) {
			if (!seenIds.has(mr.id)) {
				seenIds.add(mr.id);
				allOpenMRs.push(mr);
			}
		}

		// Process open MRs
		for (const mr of allOpenMRs) {
			// Check if ignored
			if (ignoredIds.includes(mr.id)) {
				columns.ignored.push(mr);
				continue;
			}

			// Determine pipeline status (gitdash uses headPipeline object)
			const pipelineStatus = mr.headPipeline?.status?.toLowerCase();
			const pipelineSuccess =
				pipelineStatus === "success" || pipelineStatus === "passed";

			// Check if all merge checks pass
			const allChecksPassed =
				pipelineSuccess &&
				!mr.conflicts &&
				mr.approvalsLeft === 0 &&
				!mr.draft;

			if (allChecksPassed) {
				columns.readyToMerge.push(mr);
				continue;
			}

			// Check if I approved but something is blocking
			// In gitdash, myReview === 'APPROVED' indicates I approved
			const iHaveApproved = mr.myReview === "APPROVED";

			if (iHaveApproved) {
				columns.approvedBlocked.push(mr);
				continue;
			}

			// Default: Open & Assigned
			columns.openAssigned.push(mr);
		}

		// Process closed MRs
		// Note: Previously ignored MRs that are now closed go to recentlyClosed
		for (const mr of recentlyClosedMRs) {
			columns.recentlyClosed.push(mr);

			// Remove from ignored if it was there (it's now closed)
			const ignoredIndex = columns.ignored.findIndex((m) => m.id === mr.id);
			if (ignoredIndex !== -1) {
				columns.ignored.splice(ignoredIndex, 1);
			}
		}

		// Sort all columns by selected date field (most recent first), drafts at bottom
		const sorter = createSorter(sortOption);
		Object.keys(columns).forEach((key) => {
			columns[key].sort(sorter);
		});

		return columns;
	}, [pendingReviewMRs, myOpenMRs, recentlyClosedMRs, ignoredIds, sortOption]);

	// Column metadata for rendering
	const columnConfig = [
		{
			id: "ignored",
			title: "Ignored",
			color: "var(--color-col-ignored)",
			mrs: categorized.ignored,
			acceptsDrop: true,
			description: "MRs you have chosen to ignore",
		},
		{
			id: "openAssigned",
			title: "Open & Assigned",
			color: "var(--color-col-open)",
			mrs: categorized.openAssigned,
			acceptsDrop: false,
			description: "MRs awaiting your review",
		},
		{
			id: "approvedBlocked",
			title: "Approved, Pending",
			color: "var(--color-col-pending)",
			mrs: categorized.approvedBlocked,
			acceptsDrop: false,
			description: "You approved, waiting on other checks",
		},
		{
			id: "readyToMerge",
			title: "Ready to Merge",
			color: "var(--color-col-ready)",
			mrs: categorized.readyToMerge,
			acceptsDrop: false,
			description: "All checks pass, ready to merge",
		},
		{
			id: "recentlyClosed",
			title: "Recently Closed",
			color: "var(--color-col-closed)",
			mrs: categorized.recentlyClosed,
			acceptsDrop: false,
			description: "Merged or closed recently",
		},
	];

	const totalCount =
		pendingReviewMRs.length + myOpenMRs.length + recentlyClosedMRs.length;
	const openCount =
		pendingReviewMRs.length + myOpenMRs.length - categorized.ignored.length;

	return {
		columns: columnConfig,
		totalCount,
		openCount,
	};
};
